import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import Select from 'react-select';
import { Button as ChakraButton } from '@chakra-ui/react';
import { FaFolderMinus, FaFolderPlus, FaHistory, FaSyncAlt } from 'react-icons/fa';
import TreeView from '@material-ui/lab/TreeView';
import TreeItem from '@material-ui/lab/TreeItem';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ArrowRightIcon from '@material-ui/icons/ArrowRight';
import Swal from 'sweetalert2';
import { Card, Col, Form, Row } from 'react-bootstrap';

import { ApplicationState } from '../../../store';
import { CompanyMaster } from '../../../models/CompanyMaster';
import { FolderMap } from '../../../models/Folder';
import { useAdminContext } from '../../../context/adminProvider';
import { logAdminAction } from '../../../helpers/adminLog';
import AdminActionLogModal from '../../log/AdminActionLogModal';

interface SelectOption { value: string; label: string; }

const selectMenuStyle = { menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) };

const MONTHS = [
    '01-January', '02-February', '03-March', '04-April',
    '05-May', '06-June', '07-July', '08-August',
    '09-September', '10-October', '11-November', '12-December',
];

const INT_TO_ROMAN: Record<number, string> = {
    1: 'I', 2: 'II', 3: 'III', 4: 'IV', 5: 'V',
    6: 'VI', 7: 'VII', 8: 'VIII', 9: 'IX', 10: 'X',
    11: 'XI', 12: 'XII', 13: 'XIII', 14: 'XIV', 15: 'XV',
    16: 'XVI', 17: 'XVII', 18: 'XVIII', 19: 'XIX', 20: 'XX',
};
const ROMAN_TO_INT: Record<string, number> = Object.entries(INT_TO_ROMAN).reduce(
    (acc, [k, v]) => { acc[v] = parseInt(k); return acc; }, {} as Record<string, number>
);

function parseSuffix(nodeCode: string): number | null {
    const i = nodeCode.lastIndexOf('_');
    if (i < 0 || i === nodeCode.length - 1) return null;
    return ROMAN_TO_INT[nodeCode.substring(i + 1)] ?? null;
}

function generateChildNodeCode(parentCode: string, segment: string): string {
    const level = parseSuffix(parentCode);
    if (level === null) return '';
    const nextRoman = INT_TO_ROMAN[level + 1];
    if (!nextRoman) return '';
    const base = parentCode.substring(0, parentCode.lastIndexOf('_'));
    return `${base}-${segment}_${nextRoman}`;
}

function token() { return localStorage.getItem('DMS_APPLICATION') ?? ''; }

function collectAllNodeCodes(nodes: FolderMap[]): Set<string> {
    const codes = new Set<string>();
    function walk(list: FolderMap[]) {
        for (const n of list) {
            codes.add(n.node_code);
            if (n.list_node?.length) walk(n.list_node);
        }
    }
    walk(nodes);
    return codes;
}

function findNodeChildren(nodes: FolderMap[], targetCode: string): FolderMap[] {
    for (const n of nodes) {
        if (n.node_code === targetCode) return n.list_node ?? [];
        const found = findNodeChildren(n.list_node ?? [], targetCode);
        if (found.length > 0 || n.node_code === targetCode) return found;
    }
    return [];
}

function collectAllNodeIds(nodes: FolderMap[]): string[] {
    const ids: string[] = [];
    function walk(list: FolderMap[]) {
        for (const n of list) {
            ids.push(n.node_code);
            if (n.list_node?.length) walk(n.list_node);
        }
    }
    walk(nodes);
    return ids;
}

// กรอง Archive Zone / Focus One ERP ให้เหลือเฉพาะ folder ของบริษัทที่เลือก
// เหมือน logic ใน FoldersComponent.tsx
function filterByCompanyShortName(nodes: FolderMap[], shortName: string): FolderMap[] {
    const data: FolderMap[] = JSON.parse(JSON.stringify(nodes));
    const systemNode = data.find(n => n.folder_name === 'System' && n.node_code === 'SYS_');
    if (!systemNode?.list_node) return data;

    const archiveZone = systemNode.list_node.find(n => n.folder_name === 'Archive Zone');
    const focusOneERP = systemNode.list_node.find(n => n.folder_name === 'Focus One ERP');

    if (archiveZone?.list_node) {
        archiveZone.list_node = archiveZone.list_node.filter(n => n.folder_name === shortName);
    }
    if (focusOneERP?.list_node) {
        focusOneERP.list_node = focusOneERP.list_node.filter(n => n.folder_name === shortName);
    }

    return data;
}

interface PreviewItem { name: string; code: string; exists: boolean; }

function AnnualFolderCreator() {
    const companies = useSelector<ApplicationState, CompanyMaster[]>(
        state => state.companyMaster?.responseCompanyMaster?.result_list ?? []
    );
    const { userRole } = useAdminContext();

    const companyOptions: SelectOption[] = useMemo(() => {
        if (companies.length === 0 || userRole.length === 0) return [];
        const hasMasterSid = userRole.some(r => r.sid === '*');
        if (hasMasterSid) return companies.map(c => ({ value: c.sid, label: c.company_name }));
        const allowedSids = new Set(userRole.map(r => r.sid));
        return companies.filter(c => allowedSids.has(c.sid)).map(c => ({ value: c.sid, label: c.company_name }));
    }, [companies, userRole]);

    const isCompanyLocked = companyOptions.length === 1;

    const [selectedSid, setSelectedSid] = useState('');
    const [tree, setTree] = useState<FolderMap[]>([]);
    const [expanded, setExpanded] = useState<string[]>([]);
    const [isLoadingTree, setIsLoadingTree] = useState(false);
    const [selectedFolder, setSelectedFolder] = useState<{ node_code: string; folder_name: string } | null>(null);
    const [year, setYear] = useState<number>(new Date().getFullYear() + 1);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [deleteYearOption, setDeleteYearOption] = useState<SelectOption | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isLogOpen, setIsLogOpen] = useState(false);

    useEffect(() => {
        if (isCompanyLocked) setSelectedSid(companyOptions[0].value);
    }, [companyOptions, isCompanyLocked]);

    const loadTree = useCallback((sid: string) => {
        setIsLoadingTree(true);
        setSelectedFolder(null);
        const shortName = companies.find(c => c.sid === sid)?.company_short_name;
        fetch(`/v1/folder/map?app_id=${sid}`, { headers: { Authorization: `Bearer ${token()}` } })
            .then(r => r.json())
            .then((data: FolderMap[]) => {
                let nodes = (data ?? []).filter(n => n.node_code === 'SYS_');
                if (shortName) {
                    nodes = filterByCompanyShortName(nodes, shortName);
                }
                setTree(nodes);
                setExpanded(collectAllNodeIds(nodes));
            })
            .catch(() => setTree([]))
            .finally(() => setIsLoadingTree(false));
    }, [companies]);

    useEffect(() => {
        if (!selectedSid) { setTree([]); setSelectedFolder(null); return; }
        loadTree(selectedSid);
    }, [selectedSid, loadTree]);

    const existingCodes = useMemo(() => collectAllNodeCodes(tree), [tree]);

    const yearNodeCode = useMemo(() => {
        if (!selectedFolder) return '';
        return generateChildNodeCode(selectedFolder.node_code, String(year));
    }, [selectedFolder, year]);

    const yearExists = useMemo(() => !!yearNodeCode && existingCodes.has(yearNodeCode), [yearNodeCode, existingCodes]);

    const previewMonths: PreviewItem[] = useMemo(() => {
        if (!yearNodeCode) return [];
        return MONTHS.map(m => {
            const code = generateChildNodeCode(yearNodeCode, m);
            return { name: m, code, exists: existingCodes.has(code) };
        });
    }, [yearNodeCode, existingCodes]);

    const newCount = previewMonths.filter(m => !m.exists).length + (yearExists ? 0 : 1);
    const existCount = previewMonths.filter(m => m.exists).length + (yearExists ? 1 : 0);
    const hasParentRoman = selectedFolder ? parseSuffix(selectedFolder.node_code) !== null : false;

    const handleConfirm = useCallback(async () => {
        if (!selectedFolder || !year) return;

        const toCreate = newCount;
        const toSkip = existCount;
        const result = await Swal.fire({
            title: `สร้าง Folder ปี ${year}?`,
            html: `
                <div style="text-align:left;font-size:14px">
                    <b>Parent:</b> ${selectedFolder.node_code}<br/>
                    <b>Year folder:</b> ${yearNodeCode}<br/><br/>
                    🆕 สร้างใหม่: <b>${toCreate}</b> folder<br/>
                    ✅ มีอยู่แล้ว (ข้าม): <b>${toSkip}</b> folder
                </div>`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'สร้าง',
            cancelButtonText: 'ยกเลิก',
        });
        if (!result.isConfirmed) return;

        setIsSubmitting(true);
        try {
            const res = await fetch(`/v1/folder/add-year?app_id=${selectedSid}`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ node_parent: selectedFolder.node_code, year }),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err?.message ?? 'บันทึกไม่สำเร็จ');
            }
            await Swal.fire({
                title: 'สำเร็จ',
                text: `สร้าง ${toCreate} folder ใหม่ เรียบร้อยแล้ว`,
                icon: 'success',
                timer: 2000,
                showConfirmButton: false,
            });
            logAdminAction('ADM-DMS105', 'CREATE_YEAR', yearNodeCode, selectedSid,
                `year=${year}, parent=${selectedFolder.node_code}, new=${toCreate}`);
            loadTree(selectedSid);
        } catch (err: any) {
            Swal.fire('Error', err.message ?? 'เกิดข้อผิดพลาด', 'error');
        } finally {
            setIsSubmitting(false);
        }
    }, [selectedFolder, year, yearNodeCode, selectedSid, newCount, existCount, loadTree]);

    const existingYearOptions: SelectOption[] = useMemo(() => {
        if (!selectedFolder) return [];
        const children = findNodeChildren(tree, selectedFolder.node_code);
        return children
            .filter(n => /^\d{4}$/.test(n.folder_name))
            .map(n => ({ value: n.node_code, label: n.folder_name }));
    }, [selectedFolder, tree]);

    const handleDeleteYear = useCallback(async () => {
        if (!deleteYearOption || !selectedSid) return;

        const yearLabel = deleteYearOption.label;
        const yearNodeCodeVal = deleteYearOption.value;

        const first = await Swal.fire({
            title: `ลบ Folder ปี ${yearLabel}?`,
            html: `
                <div style="text-align:left;font-size:14px">
                    จะลบ Folder ปี <b>${yearLabel}</b> และ Folder เดือนทั้งหมด (สูงสุด 12 folders)<br/><br/>
                    <span style="color:#c53030">⚠️ การลบนี้ไม่สามารถย้อนกลับได้</span>
                </div>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#e53e3e',
            confirmButtonText: 'ลบ',
            cancelButtonText: 'ยกเลิก',
        });
        if (!first.isConfirmed) return;

        const second = await Swal.fire({
            title: 'ยืนยันการลบอีกครั้ง',
            html: `
                <div style="text-align:left;font-size:14px">
                    คุณแน่ใจหรือไม่ว่าต้องการลบ Folder ปี <b>${yearLabel}</b><br/>
                    <code style="font-size:11px;color:#555">${yearNodeCodeVal}</code><br/><br/>
                    <span style="color:#c53030;font-weight:600">ข้อมูลทั้งหมดใน Folder นี้จะถูกลบถาวร</span>
                </div>`,
            icon: 'error',
            showCancelButton: true,
            confirmButtonColor: '#c53030',
            confirmButtonText: 'ยืนยัน ลบถาวร',
            cancelButtonText: 'ยกเลิก',
        });
        if (!second.isConfirmed) return;

        setIsDeleting(true);
        try {
            const res = await fetch(`/v1/folder/delete-year?app_id=${selectedSid}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ year_node_code: yearNodeCodeVal }),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err?.message ?? 'ลบไม่สำเร็จ');
            }
            await Swal.fire({
                title: 'ลบสำเร็จ',
                text: `ลบ Folder ปี ${yearLabel} เรียบร้อยแล้ว`,
                icon: 'success',
                timer: 2000,
                showConfirmButton: false,
            });
            logAdminAction('ADM-DMS105', 'DELETE_YEAR', yearNodeCodeVal, selectedSid, `year=${yearLabel}`);
            setDeleteYearOption(null);
            loadTree(selectedSid);
        } catch (err: any) {
            Swal.fire('Error', err.message ?? 'เกิดข้อผิดพลาด', 'error');
        } finally {
            setIsDeleting(false);
        }
    }, [deleteYearOption, selectedSid, loadTree]);

    const renderTree = (nodes: FolderMap[]): React.ReactNode =>
        nodes.map(node => {
            const isSelected = selectedFolder?.node_code === node.node_code;
            return (
                <TreeItem
                    key={node.node_code}
                    nodeId={node.node_code}
                    label={
                        <span
                            onClick={e => { e.stopPropagation(); setSelectedFolder({ node_code: node.node_code, folder_name: node.folder_name }); }}
                            style={{
                                fontSize: 13,
                                padding: '2px 6px',
                                borderRadius: 4,
                                cursor: 'pointer',
                                background: isSelected ? '#bee3f8' : undefined,
                                fontWeight: isSelected ? 600 : undefined,
                            }}
                        >
                            {node.folder_name}
                            <span style={{ marginLeft: 8, color: '#aaa', fontSize: 11 }}>{node.node_code}</span>
                        </span>
                    }
                >
                    {node.list_node?.length > 0 && renderTree(node.list_node)}
                </TreeItem>
            );
        });

    return (
        <>
            <div className="mt-2">
                <div className="trapezoid" style={{ width: 'auto', display: 'inline-block' }}>
                    <span className="metadata-font">ADM-DMS105 Annual Folder Management</span>
                </div>
                <div style={{ display: 'inline', float: 'right' }}>
                    <ChakraButton
                        colorScheme="purple"
                        size="sm"
                        leftIcon={<FaHistory />}
                        className="mx-1"
                        onClick={() => setIsLogOpen(true)}
                    >
                        Log
                    </ChakraButton>
                </div>
            </div>

            <AdminActionLogModal
                isOpen={isLogOpen}
                onClose={() => setIsLogOpen(false)}
                sourceModule="ADM-DMS105"
                defaultSid={selectedSid}
            />

            <div className="mat-box employee-font font-size-employee">
                <Row>
                    <Col>
                        <Row className="trapezoid trapezoid-purple" style={{ margin: 'initial' }}>
                            <span style={{ fontSize: '15px' }}>ตั้งค่า</span>
                        </Row>
                        <Card.Body
                            className="mat-box shadow"
                            style={{ borderColor: '#e5e6e9 #dfe0e4 #d0d1d5', borderStyle: 'solid', borderWidth: '1px' }}
                        >
                            <Row>
                                <Col xs={5}>
                                    <Form.Group className="mb-0">
                                        <Form.Label>บริษัท <span style={{ color: 'red' }}>*</span></Form.Label>
                                        <Select
                                            menuPortalTarget={document.body}
                                            styles={selectMenuStyle}
                                            options={companyOptions}
                                            value={companyOptions.find(o => o.value === selectedSid) ?? null}
                                            onChange={opt => { setSelectedSid(opt?.value ?? ''); }}
                                            placeholder="เลือกบริษัท"
                                            isDisabled={isCompanyLocked}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col xs={2}>
                                    <Form.Group className="mb-0">
                                        <Form.Label>ปี <span style={{ color: 'red' }}>*</span></Form.Label>
                                        <Form.Control
                                            type="number"
                                            value={year}
                                            min={2000}
                                            max={2099}
                                            onChange={e => setYear(parseInt(e.target.value) || year)}
                                        />
                                    </Form.Group>
                                </Col>
                                {selectedSid && (
                                    <Col xs="auto" className="d-flex align-items-end">
                                        <ChakraButton
                                            size="sm"
                                            variant="outline"
                                            leftIcon={<FaSyncAlt />}
                                            isLoading={isLoadingTree}
                                            onClick={() => loadTree(selectedSid)}
                                            title="Reload folder tree"
                                        >
                                            Reload
                                        </ChakraButton>
                                    </Col>
                                )}
                                {selectedFolder && existingYearOptions.length > 0 && (
                                    <>
                                        <Col xs={3}>
                                            <Form.Group className="mb-0">
                                                <Form.Label>ลบ Folder ปี</Form.Label>
                                                <Select
                                                    menuPortalTarget={document.body}
                                                    styles={selectMenuStyle}
                                                    options={existingYearOptions}
                                                    value={deleteYearOption}
                                                    onChange={opt => setDeleteYearOption(opt)}
                                                    placeholder="เลือกปีที่จะลบ"
                                                    isClearable
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col xs="auto" className="d-flex align-items-end">
                                            <ChakraButton
                                                colorScheme="red"
                                                size="sm"
                                                leftIcon={<FaFolderMinus />}
                                                isDisabled={!deleteYearOption || isDeleting}
                                                isLoading={isDeleting}
                                                loadingText="กำลังลบ..."
                                                onClick={handleDeleteYear}
                                            >
                                                ลบ Folder ปี {deleteYearOption?.label ?? ''}
                                            </ChakraButton>
                                        </Col>
                                    </>
                                )}
                            </Row>
                        </Card.Body>
                    </Col>
                </Row>

                <br />

                <Row>
                    <Col xs={5}>
                        <Row className="trapezoid trapezoid-purple" style={{ margin: 'initial' }}>
                            <span style={{ fontSize: '15px' }}>เลือก Parent Folder <span style={{ color: '#fbb' }}>*</span></span>
                        </Row>
                        <Card.Body
                            className="mat-box shadow"
                            style={{ borderColor: '#e5e6e9 #dfe0e4 #d0d1d5', borderStyle: 'solid', borderWidth: '1px', padding: 8 }}
                        >
                            <div style={{ minHeight: 360, maxHeight: 480, overflowY: 'auto' }}>
                                {!selectedSid ? (
                                    <div className="text-muted text-center pt-4">กรุณาเลือกบริษัทก่อน</div>
                                ) : isLoadingTree ? (
                                    <div className="text-muted text-center pt-4">กำลังโหลด...</div>
                                ) : tree.length === 0 ? (
                                    <div className="text-muted text-center pt-4">ไม่พบ folder</div>
                                ) : (
                                    <TreeView
                                        defaultCollapseIcon={<ArrowDropDownIcon />}
                                        defaultExpandIcon={<ArrowRightIcon />}
                                        expanded={expanded}
                                        onNodeToggle={(_, ids) => setExpanded(ids)}
                                    >
                                        {renderTree(tree)}
                                    </TreeView>
                                )}
                            </div>
                            {selectedFolder && (
                                <div className="mt-2 p-2 rounded" style={{ background: '#ebf8ff', fontSize: 12 }}>
                                    <b>เลือกแล้ว:</b> {selectedFolder.folder_name}<br />
                                    <span style={{ color: '#555' }}>{selectedFolder.node_code}</span>
                                    {!hasParentRoman && (
                                        <div style={{ color: '#c53030', marginTop: 4 }}>
                                            ⚠️ Folder นี้ไม่มี Roman numeral suffix ไม่สามารถสร้าง child ได้
                                        </div>
                                    )}
                                </div>
                            )}
                        </Card.Body>
                    </Col>

                    <Col xs={7}>
                        <Row className="trapezoid trapezoid-purple" style={{ margin: 'initial' }}>
                            <span style={{ fontSize: '15px' }}>
                                Preview Folder ที่จะสร้าง
                                {selectedFolder && yearNodeCode && (
                                    <span className="ml-3" style={{ fontSize: 12, fontWeight: 400 }}>
                                        <span style={{ color: '#bee3f8' }}>🆕 {newCount} ใหม่</span>
                                        {existCount > 0 && <span style={{ color: '#c6f6d5', marginLeft: 8 }}>✅ {existCount} มีอยู่แล้ว</span>}
                                    </span>
                                )}
                            </span>
                        </Row>
                        <Card.Body
                            className="mat-box shadow"
                            style={{ borderColor: '#e5e6e9 #dfe0e4 #d0d1d5', borderStyle: 'solid', borderWidth: '1px', padding: 12 }}
                        >
                            <div style={{ minHeight: 360, maxHeight: 480, overflowY: 'auto', fontSize: 12 }}>
                                {!selectedFolder ? (
                                    <div className="text-muted">กรุณาเลือก Parent Folder</div>
                                ) : !hasParentRoman ? (
                                    <div className="text-muted">Folder ที่เลือกไม่รองรับ</div>
                                ) : (
                                    <>
                                        <div className="mb-2 d-flex align-items-center">
                                            <span style={{ color: yearExists ? '#38a169' : '#2b6cb0', fontWeight: 600 }}>
                                                {yearExists ? '✅' : '🆕'} 📁 {year}
                                            </span>
                                            <span className="ml-2 text-muted" style={{ fontSize: 11 }}>{yearNodeCode}</span>
                                            {yearExists && <span className="ml-2 badge" style={{ background: '#c6f6d5', color: '#276749', borderRadius: 4, padding: '1px 6px' }}>มีอยู่แล้ว</span>}
                                        </div>
                                        <ul style={{ listStyle: 'none', paddingLeft: 20, margin: 0 }}>
                                            {previewMonths.map(m => (
                                                <li key={m.code} className="mb-1 d-flex align-items-center">
                                                    <span style={{ color: m.exists ? '#38a169' : '#276749', minWidth: 16 }}>
                                                        {m.exists ? '✅' : '🆕'}
                                                    </span>
                                                    <span className="ml-1" style={{ color: m.exists ? '#718096' : undefined }}>
                                                        📁 {m.name}
                                                    </span>
                                                    <span className="ml-2 text-muted" style={{ fontSize: 11 }}>{m.code}</span>
                                                    {m.exists && <span className="ml-2 badge" style={{ background: '#c6f6d5', color: '#276749', borderRadius: 4, padding: '1px 5px', fontSize: 10 }}>มีอยู่แล้ว</span>}
                                                </li>
                                            ))}
                                        </ul>
                                    </>
                                )}
                            </div>
                            <div className="mt-3">
                                <ChakraButton
                                    colorScheme="blue"
                                    leftIcon={<FaFolderPlus />}
                                    isDisabled={!selectedFolder || !year || !hasParentRoman || newCount === 0 || isSubmitting}
                                    isLoading={isSubmitting}
                                    loadingText="กำลังสร้าง..."
                                    onClick={handleConfirm}
                                >
                                    {newCount === 0
                                        ? `ปี ${year} มีครบแล้ว`
                                        : `สร้าง ${newCount} Folder ใหม่ ปี ${year}`}
                                </ChakraButton>
                            </div>
                        </Card.Body>
                    </Col>
                </Row>
            </div>
        </>
    );
}

export default AnnualFolderCreator;
